import React from "react";
import styled from "styled-components";
import { Card, RowContainer } from "../containers/styledComponents";
import { CompassParagraph } from "./styledComponents";
import { CompassGreenOutlineButton } from "../buttons/styledComponents";
import { GradientText } from "../visuals/styledComponents";
import { lightGreyText, lightGreys, CARD_GRADIENT_PALETTE } from "../../styles";

type CardVariant = "ai" | "report";

// =============================================================================
// STYLED COMPONENTS
// =============================================================================

const GradientTitle = styled(GradientText)`
  font-size: 24px;
  font-weight: bold;
  text-align: center;
`;

const StyledCard = styled(Card)<{
  $height?: number;
  $variant?: CardVariant;
  $gradientColor?: string;
}>`
  display: flex;
  flex-direction: column;
  height: ${({ $height }): number => $height || 440}px;
  width: 280px;
  box-shadow: 0px 2px 10px rgba(0, 0, 0, 0.08);
  position: relative;
  overflow: hidden;

  &::before {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: ${({ $variant, $gradientColor }): string => {
      if ($gradientColor) {
        return `linear-gradient(135deg, ${$gradientColor} 0%, ${$gradientColor}80 100%)`;
      }
      return $variant === "report"
        ? "var(--report-gradient)"
        : "linear-gradient(135deg, rgba(110, 242, 166, 0.15) 0%, rgba(50, 224, 196, 0.15) 35%, rgba(43, 179, 243, 0.15) 65%, rgba(58, 92, 245, 0.15) 100%)";
    }};
    pointer-events: none;
    z-index: 0;
  }
`;

const Separator = styled.div`
  width: 100%;
  height: 1px;
  background-color: ${lightGreys.borderLightGrey};
  margin-top: 12px;
  margin-bottom: 8px;
`;

const ImageContainer = styled.div<{ $padding?: number }>`
  width: 100%;
  height: 150px;
  overflow: hidden;
  border-radius: 12px;
  position: relative;
  background-color: white;
  padding: ${({ $padding }): string => ($padding ? `${$padding}px` : "0")};
  box-sizing: border-box;

  &::after {
    content: "";
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    border-radius: 12px;
    pointer-events: none;
    box-shadow: inset 0 0 20px 10px rgba(255, 255, 255, 0.8);
  }
`;

const StyledImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;
  object-position: center;
  display: block;
`;

// =============================================================================
// TYPES
// =============================================================================

export interface FeatureCardItem {
  title: string;
  description: string;
  icon: string;
  bg?: string;
  imagePadding?: number;
  isActive: boolean;
}

export interface FeatureCardProps {
  item: FeatureCardItem;
  /** Text for the action button */
  buttonText?: string;
  /** Text when item is inactive */
  inactiveButtonText?: string;
  /** Card height in pixels */
  height?: number;
  /** Use AI gradient instead of background image */
  useGradient?: boolean;
  /** Card variant for different gradient styles */
  variant?: "ai" | "report";
  /** Index for cycling through gradient colors (for report cards) */
  colorIndex?: number;
  /** Click handler */
  onClick: () => void;
}

// =============================================================================
// COMPONENT
// =============================================================================

/**
 * Reusable feature card component.
 * Used by ReportsList, CompassAI, and other pages that show feature grids.
 */
export const FeatureCard: React.FC<FeatureCardProps> = ({
  item,
  buttonText = "Create",
  inactiveButtonText = "Coming Soon",
  height,
  useGradient,
  variant = "ai",
  colorIndex,
  onClick,
}) => {
  const gradientColor =
    colorIndex !== undefined
      ? CARD_GRADIENT_PALETTE[colorIndex % CARD_GRADIENT_PALETTE.length]
      : undefined;

  return (
    <StyledCard
      $height={height}
      $variant={variant}
      $gradientColor={gradientColor}
    >
      {item.bg && (
        <ImageContainer $padding={item.imagePadding}>
          <StyledImage src={item.bg} alt={item.title} />
        </ImageContainer>
      )}
      <Separator />
      <RowContainer ai="center" jc="center" mt={0}>
        {useGradient ? (
          <GradientTitle>{item.title}</GradientTitle>
        ) : (
          <CompassParagraph mr={1} bold fontSize={24} ta="center">
            {item.title}
          </CompassParagraph>
        )}
      </RowContainer>
      <RowContainer ai="center" jc="center" mt={1}>
        <CompassParagraph fontSize={14} ta="center" color={lightGreyText}>
          {item.description}
        </CompassParagraph>
      </RowContainer>
      <RowContainer style={{ marginTop: "auto" }} mb={1}>
        <CompassGreenOutlineButton
          onClick={onClick}
          style={{
            opacity: item.isActive ? 1 : 0.5,
            padding: "2px 24px",
            height: "56px",
            minHeight: "auto",
            lineHeight: "1.2",
          }}
        >
          <CompassParagraph fontSize={16} bold>
            {item.isActive ? buttonText : inactiveButtonText}
          </CompassParagraph>
        </CompassGreenOutlineButton>
      </RowContainer>
    </StyledCard>
  );
};
